import {
  type BookDetail,
  type BookListResponse,
  buildPath,
  type JobSummary,
  type SessionResponse,
  type SignOutResponse,
} from "@contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, errorCodeOf, upload } from "./fetcher";
import { queryKeys } from "./keys";
import { bookListPollInterval, bookPollInterval } from "./poll";

export function useSession() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api<SessionResponse>("/session", { allowUnauthenticated: true }),
    retry: false,
    staleTime: 60_000,
  });
}

export function useSignIn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (password: string) =>
      api<SessionResponse>("/session", {
        method: "POST",
        body: { password },
        allowUnauthenticated: true,
      }),
    onSuccess: (data) => {
      // Whatever the cache holds was fetched without a session (the 401s the
      // gate answered), so it is thrown away before the Author lands on a
      // screen that would render it.
      client.clear();
      client.setQueryData(queryKeys.session, data);
    },
  });
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<SignOutResponse>("/session", { method: "DELETE" }),
    onSuccess: () => client.clear(),
  });
}

/** The dashboard list. Polls while any Book has a job running (src/client/api/poll.ts). */
export function useBooks() {
  return useQuery({
    queryKey: queryKeys.bookList,
    queryFn: () => api<BookListResponse>("/books"),
    refetchInterval: (query) => bookListPollInterval(query.state.data),
  });
}

/**
 * The Book poll target. BookLayout is the only caller: it holds this query and
 * hands the payload to every child through the context, so a Book page makes
 * one request per tick however many panels read it.
 */
export function useBook(id: string) {
  return useQuery({
    queryKey: queryKeys.book(id),
    queryFn: () => api<BookDetail>(buildPath("/books/:id", { id })),
    enabled: id.length > 0,
    refetchInterval: (query) => bookPollInterval(query.state.data),
  });
}

/**
 * What a call site wants to happen after its retry. `onSettled` runs when the
 * retry is actually over, which the service's request for confirmation is not:
 * a screen that refetches on it would otherwise refetch behind the open dialog.
 */
interface JobRetryOptions {
  readonly onSuccess?: () => void;
  readonly onSettled?: () => void;
}

/** An open question to the Author: this retry may pay for the same audio twice. */
export interface RespendConfirmation {
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly busy: boolean;
}

/**
 * POST /jobs/:id/retry: the retry button on a Failed Book and on a Chapter
 * parked after its attempts.
 *
 * The Book is refetched whether the retry was taken or refused. A 409
 * JOB_NOT_RETRYABLE means the worker already picked the job back up, so the
 * screen the Author is looking at is the stale one; refetching only on success
 * would leave a Failed banner in front of a job that is running again.
 *
 * One refusal is a question rather than an error: 409 RESPEND_NOT_CONFIRMED
 * says the job holds a paid attempt whose outcome nobody can recover, so
 * retrying it may be charged again. The hook holds that answer open in
 * `respend` and hides the refusal from `error`, so the screen asks instead of
 * showing the Author a red banner about a decision they have not made yet.
 * Every screen with a retry renders <RespendConfirmDialog> for it.
 */
export function useJobRetry() {
  const client = useQueryClient();
  const [asking, setAsking] = useState<{ jobId: string; options?: JobRetryOptions } | null>(null);
  const mutation = useMutation({
    mutationFn: ({ jobId, confirmRespend }: { jobId: string; confirmRespend?: boolean }) =>
      api<JobSummary>(buildPath("/jobs/:id/retry", { id: jobId }), {
        method: "POST",
        body: confirmRespend === true ? { confirmRespend: true } : {},
      }),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.books }),
  });
  const { mutate, reset } = mutation;
  const start = (jobId: string, confirmRespend: boolean, options?: JobRetryOptions) =>
    mutate(
      { jobId, ...(confirmRespend ? { confirmRespend } : {}) },
      {
        onSuccess: () => {
          setAsking(null);
          options?.onSuccess?.();
          options?.onSettled?.();
        },
        onError: (error) => {
          const asks = errorCodeOf(error) === "RESPEND_NOT_CONFIRMED";
          setAsking(asks ? { jobId, ...(options ? { options } : {}) } : null);
          if (!asks) options?.onSettled?.();
        },
      },
    );
  const respend: RespendConfirmation | null =
    asking === null
      ? null
      : {
          onConfirm: () => start(asking.jobId, true, asking.options),
          onCancel: () => {
            setAsking(null);
            reset();
          },
          busy: mutation.isPending,
        };
  return {
    mutate: (jobId: string, options?: JobRetryOptions) => start(jobId, false, options),
    isPending: mutation.isPending,
    // The unanswered question is not a failure to report: the dialog is.
    isError: mutation.isError && asking === null,
    error: asking === null ? mutation.error : null,
    respend,
  };
}

/**
 * POST /books with the Source. `progress` is the fraction sent so far, or null
 * while the transport reports no total, so the panel can show a busy state
 * either way.
 *
 * `cancel` aborts the request in flight, and leaving the screen aborts it too:
 * a 32 MB body on a hotel connection would otherwise leave the Author with a
 * frozen bar and a page reload as the only way out. An aborted upload resolves
 * to null rather than throwing, because a cancel is not a failure to report.
 */
export function useUploadBook() {
  const client = useQueryClient();
  const [progress, setProgress] = useState<number | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => () => inFlight.current?.abort(), []);
  const mutation = useMutation({
    mutationFn: async (file: File): Promise<BookDetail | null> => {
      const form = new FormData();
      form.append("file", file);
      const controller = new AbortController();
      inFlight.current = controller;
      try {
        return await upload<BookDetail>("/books", form, {
          onProgress: setProgress,
          signal: controller.signal,
        });
      } catch (error) {
        if (controller.signal.aborted) return null;
        throw error;
      }
    },
    onSuccess: (book) => {
      if (!book) return;
      client.setQueryData(queryKeys.book(book.id), book);
      return client.invalidateQueries({ queryKey: queryKeys.books });
    },
    onSettled: () => setProgress(null),
  });
  return { mutation, progress, cancel: () => inFlight.current?.abort() };
}
